import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "./ui"
import {
  addTaggingEvent,
  completeGtmOAuth,
  discoverTaggingEvents,
  installGtmManifest,
  listGtmContainers,
  listPaidMediaSetups,
  preflightGtmInstall,
  publishGtmInstallation,
  runGtmConfigurationAudit,
  savePaidMediaSetup,
  saveEventRoutes,
  resetOrganizationEventRoutes,
  setTaggingProfile,
  startGtmOAuth,
} from "../api/gtmManager"
import {
  completeGoogleAdsOAuth,
  listGoogleAdsConnections,
  listGoogleAdsConversionActions,
  saveGoogleAdsCustomer,
  startGoogleAdsOAuth,
} from "../api/googleAdsManager"
import { navigateCurrentWindow } from "../utils/browserNavigation"

const statusLabel = (status) => ({
  missing: "Tag missing",
  dormant: "Configured, dormant",
  active: "Configured with trigger",
  wrong_pixel: "Wrong Pixel",
  version_created: "Version ready",
  published: "Published",
  validation_failed: "Validation failed",
  failed: "Failed",
  superseded: "Configuration changed",
}[status] || status || "Not configured")

const measurementIdsFor = (value, found = new Set()) => {
  if (typeof value === "string") {
    for (const match of value.toUpperCase().matchAll(/\bG-[A-Z0-9]+\b/g)) found.add(match[0])
  } else if (Array.isArray(value)) {
    value.forEach((item) => measurementIdsFor(item, found))
  } else if (value && typeof value === "object") {
    Object.values(value).forEach((item) => measurementIdsFor(item, found))
  }
  return Array.from(found)
}

const routeMapFor = (event) => Object.fromEntries(
  (event.routes || []).map((route) => [route.platform, route]),
)

const META_EVENT_SUGGESTIONS = [
  "PageView",
  "ViewContent",
  "Search",
  "Lead",
  "Contact",
  "FindLocation",
  "Schedule",
  "AddToWishlist",
  "CustomizeProduct",
  "CompleteRegistration",
  "AddToCart",
  "InitiateCheckout",
  "AddPaymentInfo",
  "Purchase",
]

export default function PaidMediaAccountSetup({
  organizationId,
  connections,
  selectedConnectionId,
  onSelectConnection,
  onGtmAuditChange,
}) {
  const [data, setData] = useState({
    accounts: [],
    setups: [],
    gtm_connections: [],
    google_authorizations: [],
    capabilities: {},
  })
  const [selectedAccountId, setSelectedAccountId] = useState("")
  const [oauthResult, setOauthResult] = useState(null)
  const [selectedGtmAccountId, setSelectedGtmAccountId] = useState("")
  const [containers, setContainers] = useState([])
  const [selectedContainerId, setSelectedContainerId] = useState("")
  const [measurementId, setMeasurementId] = useState("")
  const [selectedGa4PropertyId, setSelectedGa4PropertyId] = useState("")
  const [selectedGoogleAdsCustomerId, setSelectedGoogleAdsCustomerId] = useState("")
  const [googleAdsOauthResult, setGoogleAdsOauthResult] = useState(null)
  const [googleAdsCustomers, setGoogleAdsCustomers] = useState([])
  const [googleAdsConnections, setGoogleAdsConnections] = useState({
    oauth_connections: [],
    customer_connections: [],
  })
  const [conversionActions, setConversionActions] = useState([])
  const [taggingEvents, setTaggingEvents] = useState([])
  const [manualEventName, setManualEventName] = useState("")
  const [triggerOptions, setTriggerOptions] = useState([])
  const [busy, setBusy] = useState("")
  const [message, setMessage] = useState(null)
  const callbackHandled = useRef(false)
  const googleAdsCallbackHandled = useRef(false)

  const reload = useCallback(async () => {
    if (!organizationId) return
    try {
      const result = await listPaidMediaSetups(organizationId)
      setData(result)
      setSelectedAccountId((current) =>
        result.accounts?.some((account) => account.id === current)
          ? current
          : result.accounts?.[0]?.id || "",
      )
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    }
  }, [organizationId])

  useEffect(() => { reload() }, [reload])

  useEffect(() => {
    if (!organizationId) return
    listGoogleAdsConnections(organizationId)
      .then(setGoogleAdsConnections)
      .catch((error) => {
        if (error.code !== "google_ads_not_configured") {
          setMessage({ type: "error", text: error.message })
        }
      })
  }, [organizationId])

  const selectedAccount = useMemo(
    () => data.accounts?.find((account) => account.id === selectedAccountId) || null,
    [data.accounts, selectedAccountId],
  )
  const setup = useMemo(
    () => data.setups?.find((item) => item.account_id === selectedAccountId) || null,
    [data.setups, selectedAccountId],
  )
  const gtmConnection = useMemo(
    () => data.gtm_connections?.find(
      (item) => item.id === setup?.gtm_container_connection_id,
    ) || null,
    [data.gtm_connections, setup],
  )
  const googleAuthorization = useMemo(
    () => data.google_authorizations?.find(
      (item) => item.id === gtmConnection?.oauth_connection_id,
    ) || null,
    [data.google_authorizations, gtmConnection],
  )
  const savedGtmAudit = setup?.gtm_configuration_audit?.result || null
  const installation = setup?.latest_installation || null
  const selectedGoogleAdsCustomer = useMemo(
    () => googleAdsConnections.customer_connections?.find(
      (item) => item.id === selectedGoogleAdsCustomerId,
    ) || data.google_ads_customers?.find(
      (item) => item.id === selectedGoogleAdsCustomerId,
    ) || null,
    [data.google_ads_customers, googleAdsConnections.customer_connections, selectedGoogleAdsCustomerId],
  )
  const legacyMappingHints = useMemo(
    () => (data.legacy_mapping_hints || []).filter(
      (hint) => hint.ga4_property_id === selectedGa4PropertyId,
    ),
    [data.legacy_mapping_hints, selectedGa4PropertyId],
  )

  useEffect(() => {
    if (!setup) {
      const firstProperty = selectedAccount?.ga4_properties?.[0]
      setSelectedGa4PropertyId(firstProperty?.id || "")
      setMeasurementId(
        measurementIdsFor(firstProperty?.ga4_property_settings)?.[0] ||
        selectedAccount?.suggested_ga4_measurement_ids?.[0] || "",
      )
      setSelectedGoogleAdsCustomerId("")
      setTaggingEvents([])
      return
    }
    setSelectedGa4PropertyId(setup.ga4_property_id || "")
    setMeasurementId(setup.ga4_measurement_id || "")
    setSelectedGoogleAdsCustomerId(setup.google_ads_customer_connection_id || "")
    setTaggingEvents(
      (data.tagging_events || []).filter((event) => event.setup_id === setup.id),
    )
    if (
      setup.meta_pixel_connection_id &&
      setup.meta_pixel_connection_id !== selectedConnectionId
    ) {
      onSelectConnection?.(setup.meta_pixel_connection_id)
    }
  }, [data.tagging_events, onSelectConnection, selectedAccount, selectedConnectionId, setup])

  useEffect(() => {
    onGtmAuditChange?.(savedGtmAudit)
  }, [onGtmAuditChange, savedGtmAudit])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const isGtmCallback =
      params.get("oauth_provider") === "gtm" ||
      sessionStorage.getItem("gtm-oauth-pending") === "true"
    const code = params.get("code")
    const state = params.get("state")
    const oauthError = params.get("error_description") || params.get("error")
    if (!isGtmCallback || (!code && !oauthError) || callbackHandled.current || !organizationId) {
      return
    }
    callbackHandled.current = true
    sessionStorage.removeItem("gtm-oauth-pending")
    setBusy("oauth")
    window.history.replaceState({}, document.title, window.location.pathname)
    if (oauthError) {
      setMessage({ type: "error", text: oauthError })
      setBusy("")
      return
    }
    completeGtmOAuth(organizationId, code, state)
      .then((result) => {
        setOauthResult(result)
        setSelectedGtmAccountId(result.accounts?.[0]?.accountId || "")
        setMessage({
          type: "success",
          text: `Google Tag Manager connected as ${result.oauth_connection?.google_email || "Google user"}.`,
        })
        return reload()
      })
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setBusy(""))
  }, [organizationId, reload])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const isGoogleAdsCallback =
      params.get("oauth_provider") === "google_ads" ||
      sessionStorage.getItem("google-ads-oauth-pending") === "true"
    const code = params.get("code")
    const state = params.get("state")
    const oauthError = params.get("error_description") || params.get("error")
    if (!isGoogleAdsCallback || (!code && !oauthError) ||
        googleAdsCallbackHandled.current || !organizationId) return
    googleAdsCallbackHandled.current = true
    sessionStorage.removeItem("google-ads-oauth-pending")
    setBusy("google-ads-oauth")
    window.history.replaceState({}, document.title, window.location.pathname)
    if (oauthError) {
      setMessage({ type: "error", text: oauthError })
      setBusy("")
      return
    }
    completeGoogleAdsOAuth(organizationId, code, state)
      .then((result) => {
        setGoogleAdsOauthResult(result.connection)
        setGoogleAdsCustomers(result.customers || [])
        setMessage({
          type: "success",
          text: `Google Ads connected as ${result.connection?.google_email || "Google user"}.`,
        })
      })
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setBusy(""))
  }, [organizationId])

  useEffect(() => {
    if (!organizationId || !selectedGoogleAdsCustomerId) {
      setConversionActions([])
      return
    }
    let cancelled = false
    listGoogleAdsConversionActions(organizationId, selectedGoogleAdsCustomerId)
      .then((result) => {
        if (cancelled) return
        const actions = result.conversion_actions || []
        const available = new Set(actions.map((action) => action.resource_name))
        const validatedAt = new Date().toISOString()
        setConversionActions(actions)
        setTaggingEvents((current) => current.map((event) => ({
          ...event,
          routes: (event.routes || []).map((route) => route.platform === "google_ads"
            ? {
                ...route,
                destination_available: available.has(route.external_action_resource_name),
                last_validated_at: validatedAt,
              }
            : route),
        })))
      })
      .catch((error) => {
        if (!cancelled) setMessage({ type: "error", text: error.message })
      })
    return () => { cancelled = true }
  }, [organizationId, selectedGoogleAdsCustomerId])

  useEffect(() => {
    if (!oauthResult?.oauth_connection?.id || !selectedGtmAccountId) {
      setContainers([])
      setSelectedContainerId("")
      return
    }
    let cancelled = false
    setBusy("containers")
    listGtmContainers(
      organizationId,
      oauthResult.oauth_connection.id,
      selectedGtmAccountId,
    )
      .then((result) => {
        if (cancelled) return
        setContainers(result.containers || [])
        setSelectedContainerId(result.containers?.[0]?.containerId || "")
      })
      .catch((error) => {
        if (!cancelled) setMessage({ type: "error", text: error.message })
      })
      .finally(() => {
        if (!cancelled) setBusy("")
      })
    return () => { cancelled = true }
  }, [oauthResult, organizationId, selectedGtmAccountId])

  const startOAuth = async (purpose = "connect") => {
    setBusy("oauth")
    setMessage(null)
    try {
      const result = await startGtmOAuth(organizationId, {
        purpose,
        oauthConnectionId: purpose === "publish" ? googleAuthorization?.id : null,
      })
      sessionStorage.setItem("gtm-oauth-pending", "true")
      navigateCurrentWindow(result.authorization_url)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
      setBusy("")
    }
  }

  const startAdsOAuth = async () => {
    setBusy("google-ads-oauth")
    setMessage(null)
    try {
      const result = await startGoogleAdsOAuth(organizationId)
      sessionStorage.setItem("google-ads-oauth-pending", "true")
      navigateCurrentWindow(result.authorization_url)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
      setBusy("")
    }
  }

  const selectGa4Property = (propertyId) => {
    setSelectedGa4PropertyId(propertyId)
    const property = selectedAccount?.ga4_properties?.find((item) => item.id === propertyId)
    const suggested = measurementIdsFor(property?.ga4_property_settings)?.[0]
    if (suggested) setMeasurementId(suggested)
  }

  const chooseGoogleAdsCustomer = async (customerId) => {
    if (!googleAdsOauthResult?.id || !customerId) return
    setBusy("google-ads-save")
    setMessage(null)
    try {
      const result = await saveGoogleAdsCustomer(
        organizationId,
        googleAdsOauthResult.id,
        customerId,
      )
      const connectionId = result.customer_connection.id
      setSelectedGoogleAdsCustomerId(connectionId)
      const connections = await listGoogleAdsConnections(organizationId)
      setGoogleAdsConnections(connections)
      if (setup) await updateSetup({ googleAdsCustomerConnectionId: connectionId })
      setMessage({ type: "success", text: "Google Ads advertiser connected." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const chooseTaggingProfile = async (profile) => {
    setBusy("profile")
    setMessage(null)
    try {
      await setTaggingProfile(organizationId, profile)
      await reload()
      setMessage({ type: "success", text: `${profile === "automotive" ? "Automotive" : "General"} tagging profile selected.` })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const resetAllRoutes = async () => {
    if (!window.confirm(
      "Reset every account's event routes for this organization? Published GTM versions remain live until a replacement is published.",
    )) return
    setBusy("reset-routes")
    setMessage(null)
    try {
      await resetOrganizationEventRoutes(organizationId)
      setTaggingEvents([])
      await reload()
      setMessage({ type: "success", text: "Organization event routes reset. You can now change the tagging profile." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const saveSetup = async () => {
    const container = containers.find((item) => item.containerId === selectedContainerId)
    const gtmAccount = oauthResult?.accounts?.find(
      (item) => item.accountId === selectedGtmAccountId,
    )
    if (!selectedAccountId || !selectedGa4PropertyId || !container) return
    setBusy("save")
    setMessage(null)
    try {
      await savePaidMediaSetup(organizationId, {
        account_id: selectedAccountId,
        meta_pixel_connection_id: selectedConnectionId || null,
        oauth_connection_id: oauthResult.oauth_connection.id,
        gtm_account_id: selectedGtmAccountId,
        gtm_account_name: gtmAccount?.name,
        container,
        ga4_property_id: selectedGa4PropertyId,
        ga4_measurement_id: measurementId,
        google_ads_customer_connection_id: selectedGoogleAdsCustomerId || null,
      })
      await reload()
      setMessage({ type: "success", text: "Paid media account setup saved." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const updateSetup = async ({ googleAdsCustomerConnectionId } = {}) => {
    if (!setup || !gtmConnection) return
    setBusy("save")
    setMessage(null)
    try {
      await savePaidMediaSetup(organizationId, {
        account_id: selectedAccountId,
        meta_pixel_connection_id: selectedConnectionId || null,
        oauth_connection_id: gtmConnection.oauth_connection_id,
        gtm_account_id: gtmConnection.gtm_account_id,
        gtm_account_name: gtmConnection.gtm_account_name,
        container: {
          accountId: gtmConnection.gtm_account_id,
          containerId: gtmConnection.container_id,
          name: gtmConnection.container_name,
          publicId: gtmConnection.public_id,
          usageContext: gtmConnection.usage_context,
        },
        ga4_property_id: selectedGa4PropertyId,
        ga4_measurement_id: measurementId,
        google_ads_customer_connection_id:
          googleAdsCustomerConnectionId || selectedGoogleAdsCustomerId || null,
      })
      await reload()
      setMessage({ type: "success", text: "Paid media account setup updated." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const discoverEvents = async () => {
    if (!setup) return
    setBusy("discover")
    setMessage(null)
    try {
      const result = await discoverTaggingEvents(organizationId, setup.id)
      setTaggingEvents(result.events || [])
      setTriggerOptions(result.trigger_options || [])
      await reload()
      setMessage({ type: "success", text: "GA4 and GTM event inventory refreshed." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const addEvent = async () => {
    if (!setup || !manualEventName.trim()) return
    setBusy("add-event")
    setMessage(null)
    try {
      const result = await addTaggingEvent(organizationId, setup.id, manualEventName.trim())
      setTaggingEvents((current) => [
        ...current.filter((event) => event.id !== result.event.id),
        result.event,
      ].sort((left, right) => left.ga4_event_name.localeCompare(right.ga4_event_name)))
      setManualEventName("")
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const changeEvent = (eventId, updater) => {
    setTaggingEvents((current) => current.map((event) =>
      event.id === eventId ? updater(event) : event,
    ))
  }

  const changeTrigger = (eventId, triggerId) => changeEvent(eventId, (event) => ({
    ...event,
    source_kind: "manual_trigger",
    trigger_ids: triggerId ? [triggerId] : [],
  }))

  const changeRoute = (eventId, platform, changes) => changeEvent(eventId, (event) => {
    const routes = routeMapFor(event)
    const previous = routes[platform] || {
      platform,
      enabled: false,
      destination_event_name: "",
      parameter_mappings: {},
    }
    routes[platform] = { ...previous, ...changes }
    return { ...event, routes: Object.values(routes) }
  })

  const selectConversionAction = (eventId, resourceName) => {
    const selected = conversionActions.find((action) => action.resource_name === resourceName)
    changeRoute(eventId, "google_ads", selected ? {
      enabled: true,
      destination_event_name: selected.name,
      external_action_resource_name: selected.resource_name,
      external_action_id: selected.id,
      conversion_id: selected.conversion_id,
      conversion_label: selected.conversion_label,
      destination_available: true,
      last_validated_at: new Date().toISOString(),
      parameter_mappings: routeMapFor(
        taggingEvents.find((event) => event.id === eventId) || {},
      ).google_ads?.parameter_mappings || {
        value: "eventModel.value",
        currencyCode: "eventModel.currency",
        orderId: "eventModel.transaction_id",
      },
    } : {
      enabled: false,
      destination_event_name: "",
      external_action_resource_name: null,
      external_action_id: null,
      conversion_id: null,
      conversion_label: null,
    })
  }

  const saveRoutes = async () => {
    if (!setup) return
    setBusy("routes")
    setMessage(null)
    try {
      const events = taggingEvents.map((event) => ({
        id: event.id,
        source_kind: event.source_kind,
        trigger_ids: event.trigger_ids || [],
        ga4_parameter_mappings: event.ga4_parameter_mappings || {},
        routes: routeMapFor(event),
      }))
      const result = await saveEventRoutes(organizationId, setup.id, events)
      setTaggingEvents(result.events || taggingEvents)
      await reload()
      setMessage({ type: "success", text: "Event routes saved." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const auditConfiguration = async () => {
    if (!setup) return
    setBusy("audit")
    setMessage(null)
    try {
      const result = await runGtmConfigurationAudit(organizationId, setup.id)
      onGtmAuditChange?.(result.audit)
      await reload()
      setMessage({ type: "success", text: "GTM configuration audit completed." })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const installManagedTags = async () => {
    if (!setup) return
    setBusy("install")
    setMessage(null)
    try {
      const preflight = await preflightGtmInstall(organizationId, setup.id)
      if (!preflight.install_enabled) {
        throw new Error("Managed GTM installation is not enabled for this environment.")
      }
      if (!preflight.ready) {
        throw new Error(
          preflight.conflicts?.length
            ? `Resolve GTM name conflicts first: ${preflight.conflicts.join(", ")}`
            : "GTM preflight did not pass.",
        )
      }
      if (!window.confirm(
        "Create the ASC TuneUp managed GTM workspace and validated container version? This does not publish it.",
      )) return
      await installGtmManifest(organizationId, setup.id)
      await reload()
      setMessage({
        type: "success",
        text: "Validated GTM container version created. Publishing still requires confirmation.",
      })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const publish = async () => {
    if (!setup || !installation || !window.confirm(
      "Publish this validated GTM container version to the live website?",
    )) return
    setBusy("publish")
    setMessage(null)
    try {
      await publishGtmInstallation(organizationId, setup.id, installation.id)
      await reload()
      setMessage({ type: "success", text: "GTM container version published." })
    } catch (error) {
      if (error.code === "gtm_publish_scope_required") {
        setMessage({
          type: "info",
          text: "Google requires a separate publishing authorization. Approve it, then click Publish again.",
        })
        await startOAuth("publish")
      } else {
        setMessage({ type: "error", text: error.message })
      }
    } finally {
      setBusy("")
    }
  }

  return (
    <Card className="paid-media-meta__account-setup">
      <CardHeader split>
        <div>
          <CardTitle>Account paid media setup</CardTitle>
          <CardDescription>
            Use GA4 as the baseline, then route the same GTM events to Meta and Google Ads.
          </CardDescription>
        </div>
        {setup && <Badge variant="subtle">Setup saved</Badge>}
      </CardHeader>
      <CardContent>
        {message && (
          <div
            className={`paid-media-meta__status paid-media-meta__status--${message.type}`}
            role={message.type === "error" ? "alert" : "status"}
          >
            {message.text}
          </div>
        )}
        <div className="paid-media-meta__connected-gtm">
          <div>
            <strong>Organization tagging profile</strong>
            <span>
              {data.tagging_profile
                ? `${data.tagging_profile === "automotive" ? "Automotive" : "General"} · inherited by every account`
                : "An organization admin must choose a profile before managed versions can be created."}
            </span>
          </div>
          <div className="paid-media-meta__toolbar-actions">
            <Button
              variant={data.tagging_profile === "automotive" ? "primary" : "outline"}
              size="sm"
              onClick={() => chooseTaggingProfile("automotive")}
              disabled={Boolean(busy) || data.tagging_profile === "automotive"}
            >
              Automotive
            </Button>
            <Button
              variant={data.tagging_profile === "general" ? "primary" : "outline"}
              size="sm"
              onClick={() => chooseTaggingProfile("general")}
              disabled={Boolean(busy) || data.tagging_profile === "general"}
            >
              General
            </Button>
            {Boolean(data.tagging_events?.length) && (
              <Button
                variant="destructive"
                size="sm"
                onClick={resetAllRoutes}
                disabled={Boolean(busy)}
              >
                Reset all routes
              </Button>
            )}
          </div>
        </div>

        {legacyMappingHints.length > 0 && (
          <p className="paid-media-meta__secondary">
            Legacy mapping hint: {legacyMappingHints.map((hint) =>
              `${hint.platform} — ${hint.external_account_name || hint.external_account_id}`
            ).join("; ")}. Reconnect and verify this destination before routing.
          </p>
        )}
        <div className="paid-media-meta__setup-grid">
          <label className="paid-media-meta__select-field">
            <span>ASC TuneUp account</span>
            <select
              value={selectedAccountId}
              onChange={(event) => setSelectedAccountId(event.target.value)}
            >
              {data.accounts?.map((account) => (
                <option value={account.id} key={account.id}>{account.name}</option>
              ))}
            </select>
          </label>
          <label className="paid-media-meta__select-field">
            <span>GA4 property</span>
            <select
              value={selectedGa4PropertyId}
              onChange={(event) => selectGa4Property(event.target.value)}
            >
              <option value="">Select a GA4 property</option>
              {selectedAccount?.ga4_properties?.map((property) => (
                <option value={property.id} key={property.id}>
                  {property.property_name || property.property_id}
                </option>
              ))}
            </select>
          </label>
          <label className="paid-media-meta__select-field">
            <span>Meta Pixel</span>
            <select
              value={selectedConnectionId}
              onChange={(event) => onSelectConnection?.(event.target.value)}
            >
              <option value="">Select a Pixel</option>
              {connections.map((connection) => (
                <option value={connection.id} key={connection.id}>
                  {connection.pixel_name} ({connection.pixel_id})
                </option>
              ))}
            </select>
          </label>
          <label className="paid-media-meta__select-field">
            <span>GA4 measurement ID</span>
            <input
              value={measurementId}
              onChange={(event) => setMeasurementId(event.target.value.toUpperCase())}
              placeholder="G-XXXXXXXXXX"
              list="paid-media-ga4-suggestions"
            />
            <datalist id="paid-media-ga4-suggestions">
              {selectedAccount?.suggested_ga4_measurement_ids?.map((id) => (
                <option value={id} key={id} />
              ))}
            </datalist>
          </label>
          <label className="paid-media-meta__select-field">
            <span>Google Ads advertiser</span>
            <select
              value={selectedGoogleAdsCustomerId}
              onChange={(event) => setSelectedGoogleAdsCustomerId(event.target.value)}
            >
              <option value="">No Google Ads destination</option>
              {(googleAdsConnections.customer_connections || []).map((customer) => (
                <option value={customer.id} key={customer.id}>
                  {customer.customer_name} ({customer.customer_id})
                </option>
              ))}
            </select>
          </label>
        </div>

        {gtmConnection ? (
          <div className="paid-media-meta__connected-gtm">
            <div>
              <strong>{gtmConnection.container_name}</strong>
              <span>
                {gtmConnection.public_id || gtmConnection.container_id}
                {googleAuthorization?.google_email
                  ? ` · ${googleAuthorization.google_email}`
                  : ""}
              </span>
            </div>
            <div className="paid-media-meta__toolbar-actions">
              <Button
                variant="ghost"
                onClick={updateSetup}
                disabled={Boolean(busy) || !measurementId || !selectedGa4PropertyId}
              >
                {busy === "save" ? "Saving…" : "Save setup changes"}
              </Button>
              <Button
                variant="outline"
                onClick={auditConfiguration}
                disabled={Boolean(busy)}
              >
                {busy === "audit" ? "Auditing…" : "Audit GTM configuration"}
              </Button>
              {installation?.status !== "version_created" && (
                <Button
                  onClick={installManagedTags}
                  disabled={Boolean(busy) || !data.capabilities?.managed_installation || !taggingEvents.length}
                >
                  {busy === "install"
                    ? "Creating version…"
                    : installation?.status === "published"
                      ? "Create updated version"
                      : "Install managed tags"}
                </Button>
              )}
              {installation?.status === "version_created" && (
                <Button onClick={publish} disabled={Boolean(busy)}>
                  {busy === "publish" ? "Publishing…" : "Publish version"}
                </Button>
              )}
            </div>
          </div>
        ) : oauthResult ? (
          <div className="paid-media-meta__gtm-discovery">
            <label className="paid-media-meta__select-field">
              <span>GTM account</span>
              <select
                value={selectedGtmAccountId}
                onChange={(event) => setSelectedGtmAccountId(event.target.value)}
              >
                {oauthResult.accounts?.map((account) => (
                  <option value={account.accountId} key={account.accountId}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="paid-media-meta__select-field">
              <span>Web container</span>
              <select
                value={selectedContainerId}
                onChange={(event) => setSelectedContainerId(event.target.value)}
                disabled={busy === "containers"}
              >
                {containers.map((container) => (
                  <option value={container.containerId} key={container.containerId}>
                    {container.name} ({container.publicId})
                  </option>
                ))}
              </select>
            </label>
            <Button
              onClick={saveSetup}
              disabled={Boolean(busy) || !selectedContainerId || !measurementId || !selectedGa4PropertyId}
            >
              {busy === "save" ? "Saving…" : "Save account setup"}
            </Button>
          </div>
        ) : (
          <div className="paid-media-meta__connected-gtm">
            <div>
              <strong>Google Tag Manager not connected</strong>
              <span>
                GTM uses a separate Google authorization from the existing GA4 connection.
              </span>
            </div>
            <Button onClick={() => startOAuth()} disabled={Boolean(busy)}>
              {busy === "oauth" ? "Connecting…" : "Connect Google Tag Manager"}
            </Button>
          </div>
        )}

        <div className="paid-media-meta__connected-gtm">
          <div>
            <strong>Google Ads destination</strong>
            <span>
              {selectedGoogleAdsCustomer
                ? `${selectedGoogleAdsCustomer.customer_name} (${selectedGoogleAdsCustomer.customer_id})`
                : "Connect an advertiser to resolve conversion names to IDs and labels."}
            </span>
          </div>
          {googleAdsCustomers.length ? (
            <div className="paid-media-meta__toolbar-actions">
              <select
                aria-label="Google Ads advertiser"
                defaultValue=""
                disabled={Boolean(busy)}
                onChange={(event) => chooseGoogleAdsCustomer(event.target.value)}
              >
                <option value="">Select an advertiser</option>
                {googleAdsCustomers.map((customer) => (
                  <option value={customer.customer_id} key={customer.customer_id}>
                    {customer.customer_name} ({customer.customer_id})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <Button variant="outline" onClick={startAdsOAuth} disabled={Boolean(busy)}>
              {busy === "google-ads-oauth" ? "Connecting…" : "Connect Google Ads"}
            </Button>
          )}
        </div>

        {setup && (
          <EventRoutingTable
            events={taggingEvents}
            triggerOptions={triggerOptions}
            conversionActions={conversionActions}
            metaConnected={Boolean(selectedConnectionId)}
            googleAdsConnected={Boolean(selectedGoogleAdsCustomerId)}
            busy={busy}
            onDiscover={discoverEvents}
            manualEventName={manualEventName}
            onManualEventNameChange={setManualEventName}
            onAddEvent={addEvent}
            onSave={saveRoutes}
            onTriggerChange={changeTrigger}
            onRouteChange={changeRoute}
            onGa4ParametersChange={(eventId, ga4_parameter_mappings) => changeEvent(
              eventId,
              (event) => ({ ...event, ga4_parameter_mappings }),
            )}
            onConversionChange={selectConversionAction}
          />
        )}

        {setup && (
          <div className="paid-media-meta__setup-statuses">
            <span>GTM audit: {savedGtmAudit ? "Saved" : "Not run"}</span>
            <span>
              Installation: {statusLabel(installation?.status)}
            </span>
            {installation?.version_path && (
              <span>Version: {installation.version_path.split("/").pop()}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function EventRoutingTable({
  events,
  triggerOptions,
  conversionActions,
  metaConnected,
  googleAdsConnected,
  busy,
  onDiscover,
  manualEventName,
  onManualEventNameChange,
  onAddEvent,
  onSave,
  onTriggerChange,
  onRouteChange,
  onGa4ParametersChange,
  onConversionChange,
}) {
  return (
    <section className="paid-media-meta__routing">
      <div className="paid-media-meta__toolbar">
        <div>
          <strong>GA4-first event routing</strong>
          <p className="paid-media-meta__secondary">
            GA4 is always the baseline. Enabled destinations reuse the same GTM trigger.
          </p>
        </div>
        <div className="paid-media-meta__toolbar-actions">
          <Input
            label="Exact GA4 event name"
            hideLabel
            placeholder="Add exact GA4 event name"
            value={manualEventName}
            onChange={(event) => onManualEventNameChange(event.target.value)}
          />
          <Button
            variant="outline"
            onClick={onAddEvent}
            disabled={Boolean(busy) || !manualEventName.trim()}
          >
            Add event
          </Button>
          <Button variant="outline" onClick={onDiscover} disabled={Boolean(busy)}>
            {busy === "discover" ? "Discovering…" : "Discover GA4 + GTM events"}
          </Button>
          <Button onClick={onSave} disabled={Boolean(busy) || !events.length}>
            {busy === "routes" ? "Saving…" : "Save routes"}
          </Button>
        </div>
      </div>
      {!events.length ? (
        <p className="paid-media-meta__table-message">
          Discover events after selecting the GA4 property and saving the GTM setup.
        </p>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>GA4 event</TableHeaderCell>
                <TableHeaderCell>GTM trigger</TableHeaderCell>
                <TableHeaderCell>GA4 baseline</TableHeaderCell>
                <TableHeaderCell>Meta route</TableHeaderCell>
                <TableHeaderCell>Google Ads conversion</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => {
                const routes = routeMapFor(event)
                const meta = routes.meta || {}
                const googleAds = routes.google_ads || {}
                const resolved = event.ga4_managed || (event.trigger_ids || []).length > 0
                return (
                  <TableRow key={event.id}>
                    <TableCell>
                      <strong>{event.ga4_event_name}</strong>
                      <span className="paid-media-meta__secondary">
                        {event.observed_in_ga4 ? "Observed in GA4" : "Preset or GTM only"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {event.ga4_managed && !(event.trigger_ids || []).length ? (
                        <span>Managed exact custom-event trigger</span>
                      ) : (event.trigger_ids || []).length ? (
                        <span>{(event.trigger_ids || []).join(", ")}</span>
                      ) : (
                        <select
                          aria-label={`GTM trigger for ${event.ga4_event_name}`}
                          value=""
                          onChange={(change) => onTriggerChange(event.id, change.target.value)}
                        >
                          <option value="">Select required trigger</option>
                          {triggerOptions.map((trigger) => (
                            <option value={trigger.id} key={trigger.id}>{trigger.name}</option>
                          ))}
                        </select>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="subtle">
                        {event.configured_in_gtm
                          ? "Existing GA4 tag"
                          : event.ga4_managed
                            ? "Managed GA4 tag"
                            : resolved ? "Trigger mapped" : "Needs trigger"}
                      </Badge>
                      {Object.keys(event.ga4_parameter_mappings || {}).length > 0 && (
                        <ParameterMappings
                          label="GA4 parameters"
                          mappings={event.ga4_parameter_mappings}
                          onChange={(mappings) => onGa4ParametersChange(event.id, mappings)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <label>
                        <input
                          type="checkbox"
                          checked={meta.enabled === true}
                          disabled={!metaConnected && !meta.enabled}
                          onChange={(change) => onRouteChange(event.id, "meta", {
                            enabled: change.target.checked,
                          })}
                        /> Enable
                      </label>
                      <Input
                        label={`Meta event for ${event.ga4_event_name}`}
                        hideLabel
                        value={meta.destination_event_name || ""}
                        placeholder="Lead or custom event"
                        list="paid-media-meta-event-suggestions"
                        disabled={!meta.enabled}
                        onChange={(change) => onRouteChange(event.id, "meta", {
                          destination_event_name: change.target.value,
                        })}
                      />
                      {meta.enabled && (
                        <ParameterMappings
                          label="Meta parameters"
                          mappings={meta.parameter_mappings || {}}
                          onChange={(parameter_mappings) => onRouteChange(event.id, "meta", {
                            parameter_mappings,
                          })}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <select
                        aria-label={`Google Ads conversion for ${event.ga4_event_name}`}
                        value={googleAds.external_action_resource_name || ""}
                        disabled={!googleAdsConnected}
                        onChange={(change) => onConversionChange(event.id, change.target.value)}
                      >
                        <option value="">Do not route</option>
                        {googleAds.enabled && googleAds.external_action_resource_name &&
                          !conversionActions.some(
                            (action) => action.resource_name === googleAds.external_action_resource_name,
                          ) && (
                            <option value={googleAds.external_action_resource_name} disabled>
                              Unavailable — {googleAds.destination_event_name || "previous conversion"}
                            </option>
                          )}
                        {conversionActions.map((action) => (
                          <option value={action.resource_name} key={action.resource_name}>
                            {action.name}
                          </option>
                        ))}
                      </select>
                      {googleAds.enabled && (
                        <>
                          {googleAds.destination_available === false && (
                            <Badge variant="warning">Refresh or replace unavailable action</Badge>
                          )}
                          <span className="paid-media-meta__secondary">
                            AW-{googleAds.conversion_id}/{googleAds.conversion_label}
                          </span>
                          <ParameterMappings
                            label="Google Ads parameters"
                            mappings={googleAds.parameter_mappings || {}}
                            onChange={(parameter_mappings) => onRouteChange(
                              event.id,
                              "google_ads",
                              { parameter_mappings },
                            )}
                          />
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <datalist id="paid-media-meta-event-suggestions">
        {META_EVENT_SUGGESTIONS.map((name) => <option value={name} key={name} />)}
      </datalist>
    </section>
  )
}

function ParameterMappings({ label, mappings, onChange }) {
  const entries = Object.entries(mappings)
  const renameDestination = (previous, next) => {
    const name = next.trim()
    if (!name || name === previous) return
    const updated = {}
    for (const [destination, source] of Object.entries(mappings)) {
      updated[destination === previous ? name : destination] = source
    }
    onChange(updated)
  }
  const changeSourceType = (destination, source, type) => onChange({
    ...mappings,
    [destination]: type === "constant"
      ? { constant: source && typeof source === "object" ? source.constant || "" : "" }
      : source && typeof source === "object" ? source.path || "" : source || "",
  })
  const changeSource = (destination, source, value) => onChange({
    ...mappings,
    [destination]: source && typeof source === "object" && "constant" in source
      ? { constant: value }
      : value,
  })
  const addMapping = () => {
    let index = entries.length + 1
    while (mappings[`parameter_${index}`] !== undefined) index += 1
    onChange({ ...mappings, [`parameter_${index}`]: "" })
  }
  return (
    <details className="paid-media-meta__route-parameters">
      <summary>{label}</summary>
      {entries.map(([destination, source]) => (
        <div className="paid-media-meta__route-mapping-row" key={destination}>
          <Input
            label="Destination parameter"
            defaultValue={destination}
            onBlur={(event) => renameDestination(destination, event.target.value)}
          />
          <label className="paid-media-meta__select-field">
            <span>Source type</span>
            <select
              value={source && typeof source === "object" && "constant" in source
                ? "constant"
                : "path"}
              onChange={(event) => changeSourceType(destination, source, event.target.value)}
            >
              <option value="path">Data-layer path</option>
              <option value="constant">Constant</option>
            </select>
          </label>
          <Input
            label={source && typeof source === "object" && "constant" in source
              ? "Constant value"
              : "Data-layer/event-model path"}
            value={typeof source === "string" ? source : source?.path || source?.constant || ""}
            onChange={(event) => changeSource(destination, source, event.target.value)}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(Object.fromEntries(
              Object.entries(mappings).filter(([name]) => name !== destination),
            ))}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addMapping}>Add parameter</Button>
    </details>
  )
}
